import {
  type AvailabilityFormValues,
  buildAvailabilityFormValues,
} from "@/lib/cal/availability-mapper";
import { createLogger } from "@/lib/logger";
import type { PracticeAppointmentType } from "@/models/practice/PracticeAppointmentType";

const logger = createLogger("load-practice-appointment-type-availability");

interface LoadPracticeAppointmentTypeAvailabilityInput {
  appointmentType: PracticeAppointmentType;
  practiceTimeZone?: string;
  hostTimeZone: string;
  logContext: Record<string, unknown>;
}

export interface ReadablePracticeAppointmentTypeAvailabilityResult {
  calendarReadable: true;
  availability: AvailabilityFormValues;
  hasExplicitAvailability: boolean;
}

export interface UnreadablePracticeAppointmentTypeAvailabilityResult {
  calendarReadable: false;
  availability: undefined;
  hasExplicitAvailability: false;
}

export type LoadPracticeAppointmentTypeAvailabilityResult =
  | ReadablePracticeAppointmentTypeAvailabilityResult
  | UnreadablePracticeAppointmentTypeAvailabilityResult;

/**
 * Builds the host-inherited placeholder used when appointment-type availability should not apply yet.
 */
function buildHostInheritedAvailability({
  appointmentType,
  practiceTimeZone,
  hostTimeZone,
}: {
  appointmentType: PracticeAppointmentType;
  practiceTimeZone?: string;
  hostTimeZone: string;
}): AvailabilityFormValues {
  return buildAvailabilityFormValues({
    schedule: null,
    timeZoneFallback: practiceTimeZone || hostTimeZone,
    nameOverride: appointmentType.title,
    isDefault: false,
    inheritHostAvailability: true,
  });
}

/**
 * Loads the appointment-type availability only when the schedule was explicitly saved by us.
 */
export async function loadPracticeAppointmentTypeAvailability({
  appointmentType,
  practiceTimeZone,
  hostTimeZone,
  logContext,
}: LoadPracticeAppointmentTypeAvailabilityInput): Promise<LoadPracticeAppointmentTypeAvailabilityResult> {
  if (appointmentType.type === "room") {
    return {
      calendarReadable: true,
      availability: buildHostInheritedAvailability({
        appointmentType,
        practiceTimeZone,
        hostTimeZone,
      }),
      hasExplicitAvailability: false,
    };
  }

  if (!appointmentType.hasExplicitAvailabilitySchedule) {
    // Legacy persisted snapshots are no longer enough to opt into enforcement. The explicit-save
    // flag is now the switch; rollout cleanup clears any stale Cal projection separately.
    return {
      calendarReadable: true,
      availability: buildHostInheritedAvailability({
        appointmentType,
        practiceTimeZone,
        hostTimeZone,
      }),
      hasExplicitAvailability: false,
    };
  }

  if (!appointmentType.availabilityFormValues) {
    logger.warn("Explicit practice appointment type availability is unreadable", logContext);
    return {
      calendarReadable: false,
      availability: undefined,
      hasExplicitAvailability: false,
    };
  }

  return {
    calendarReadable: true,
    availability: appointmentType.availabilityFormValues,
    hasExplicitAvailability: true,
  };
}
